import { Injectable, Inject, Optional, InjectionToken } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, tap, shareReplay } from 'rxjs/operators';
import { UserFavorite } from '../models/user-favorites/user-favorite.model';
import { SideNavItem } from '../models/sidenav-item.interface';

// InjectionToken for API base URL
export const API_BASE_URL = new InjectionToken<string>('API_BASE_URL');

@Injectable({
  providedIn: 'root'
})
export class UserFavoritesService {
  private readonly API_PATH = '/userfavorites';
  private readonly STORAGE_KEY = 'user-favorites';
  private readonly API_BASE_URL: string;

  // BehaviorSubject to maintain favorites state
  private favoritesSubject = new BehaviorSubject<UserFavorite[]>([]);
  public favorites$ = this.favoritesSubject.asObservable();

  // Request cache per userId to avoid multiple calls
  private loadCache: Map<string, Observable<UserFavorite[]>> = new Map();
  private loadedUsers: Set<string> = new Set(); // Track already loaded users

  constructor(
    private http: HttpClient,
    @Optional() @Inject(API_BASE_URL) apiBaseUrl?: string
  ) {
    // Use injected URL or fallback to localStorage/window
    this.API_BASE_URL = apiBaseUrl || this.getApiBaseUrl();
  }

  private getApiBaseUrl(): string {
    // Try to get from window/localStorage or use default
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('api_base_url');
      if (stored) return stored;
    }
    // Default fallback
    return 'http://localhost:5000/v1';
  }

  /**
   * Get HTTP headers with user_id
   */
  private getHeaders(userId: string): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'user_id': userId
    });
  }

  /**
   * Load favorites for a user - with caching to prevent multiple calls
   */
  loadFavorites(userId: string): Observable<UserFavorite[]> {
    if (!userId) {
      const local = this.getFavoritesFromLocalStorage(userId);
      this.favoritesSubject.next(local);
      return of(local);
    }

    // If already loaded for this user, return what we have in memory
    if (this.loadedUsers.has(userId)) {
      return of(this.favoritesSubject.value);
    }

    // If there's already a request in progress for this user, share the Observable
    if (this.loadCache.has(userId)) {
      return this.loadCache.get(userId)!;
    }

    const url = `${this.API_BASE_URL}${this.API_PATH}`;
    const headers = this.getHeaders(userId);

    // Create the shared Observable
    const request$ = this.http.get<any[]>(url, { headers }).pipe(
      map(response => {
        // Map backend response to UserFavorite model
        const favorites = response.map(item => {
          const fav = new UserFavorite(
            item.user_id || item.userId,
            item.display_name || item.displayName,
            item.icon_class || item.iconClass,
            item.route,
            item.order || 0
          );
          fav.id = item._id || item.id;
          return fav;
        });
        return favorites;
      }),
      tap(favorites => {
        // Mark as loaded
        this.loadedUsers.add(userId);
        // Update the subject
        this.favoritesSubject.next(favorites);
        // Save to localStorage
        this.saveFavoritesToLocalStorage(userId, favorites);
        // Clear the cache for this request
        this.loadCache.delete(userId);
      }),
      catchError(error => {
        console.error('Failed to load favorites from API:', error);
        // Clear cache on error
        this.loadCache.delete(userId);
        // Use localStorage as fallback
        const localFavorites = this.getFavoritesFromLocalStorage(userId);
        this.favoritesSubject.next(localFavorites);
        // Mark as loaded to avoid constant retries
        this.loadedUsers.add(userId);
        return of(localFavorites);
      }),
      shareReplay(1) // Share the result between multiple subscribers
    );

    // Save to cache
    this.loadCache.set(userId, request$);

    return request$;
  }

  /**
   * Add a favorite
   */
  addFavorite(userId: string, item: SideNavItem): Observable<UserFavorite> {
    const currentFavorites = this.favoritesSubject.value;
    const order = currentFavorites.length;

    const favorite = new UserFavorite(
      userId,
      item.displayName,
      item.iconClass,
      item.route,
      order
    );

    if (!userId) {
      // localStorage only
      const updated = [...currentFavorites, favorite];
      this.favoritesSubject.next(updated);
      this.saveFavoritesToLocalStorage(userId, updated);
      return of(favorite);
    }

    const url = `${this.API_BASE_URL}${this.API_PATH}`;
    const headers = this.getHeaders(userId);

    // Explicitly serialize to ensure all fields are sent
    const payload = {
      user_id: favorite.userId,
      display_name: favorite.displayName,
      icon_class: favorite.iconClass,
      route: favorite.route,
      order: favorite.order
    };

    return this.http.post<any>(url, payload, { headers }).pipe(
      map(response => {
        // Map backend response
        const savedFavorite = new UserFavorite(
          response.user_id || response.userId,
          response.display_name || response.displayName,
          response.icon_class || response.iconClass,
          response.route,
          response.order || 0
        );
        savedFavorite.id = response._id || response.id;
        return savedFavorite;
      }),
      tap(savedFavorite => {
        const updated = [...currentFavorites, savedFavorite];
        this.favoritesSubject.next(updated);
        this.saveFavoritesToLocalStorage(userId, updated);
      }),
      catchError(error => {
        console.error('Failed to add favorite:', error);
        const updated = [...currentFavorites, favorite];
        this.favoritesSubject.next(updated);
        this.saveFavoritesToLocalStorage(userId, updated);
        return of(favorite);
      })
    );
  }

  /**
   * Remove a favorite by route
   */
  removeFavorite(userId: string, route: string): Observable<void> {
    const currentFavorites = this.favoritesSubject.value;
    const favoriteToRemove = currentFavorites.find(f => f.route === route);

    if (!favoriteToRemove) {
      return of(void 0);
    }

    if (!userId || !favoriteToRemove.id) {
      // localStorage only
      const updated = currentFavorites.filter(f => f.route !== route);
      this.favoritesSubject.next(updated);
      this.saveFavoritesToLocalStorage(userId, updated);
      return of(void 0);
    }

    const url = `${this.API_BASE_URL}${this.API_PATH}/${favoriteToRemove.id}`;
    const headers = this.getHeaders(userId);

    return this.http.delete<void>(url, { headers }).pipe(
      tap(() => {
        const updated = currentFavorites.filter(f => f.route !== route);
        this.favoritesSubject.next(updated);
        this.saveFavoritesToLocalStorage(userId, updated);
      }),
      catchError(error => {
        console.error('Failed to remove favorite:', error);
        const updated = currentFavorites.filter(f => f.route !== route);
        this.favoritesSubject.next(updated);
        this.saveFavoritesToLocalStorage(userId, updated);
        return of(void 0);
      })
    );
  }

  /**
   * Check if an item is favorited
   */
  isFavorite(route: string): boolean {
    return this.favoritesSubject.value.some(f => f.route === route);
  }

  /**
   * Get current favorites as SideNavItems
   */
  getFavoritesAsMenuItems(): SideNavItem[] {
    return this.favoritesSubject.value
      .sort((a, b) => a.order - b.order)
      .map(fav => ({
        displayName: fav.displayName,
        iconClass: fav.iconClass,
        route: fav.route
      }));
  }

  /**
   * Clear cache for a user (useful for testing or logout)
   */
  clearCache(userId: string): void {
    this.loadedUsers.delete(userId);
    this.loadCache.delete(userId);
  }

  // LocalStorage helpers
  private getFavoritesFromLocalStorage(userId: string): UserFavorite[] {
    try {
      const key = `${this.STORAGE_KEY}_${userId}`;
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading favorites from localStorage', error);
      return [];
    }
  }

  private saveFavoritesToLocalStorage(userId: string, favorites: UserFavorite[]): void {
    try {
      const key = userId ? `${this.STORAGE_KEY}_${userId}` : this.STORAGE_KEY;
      localStorage.setItem(key, JSON.stringify(favorites));
    } catch (error) {
      console.error('Error saving favorites to localStorage', error);
    }
  }
}
